import { Help } from '@procore/core-icons'
import React from 'react'
import type { OverlayTriggerProps } from '../OverlayTrigger'
import { OverlayTrigger } from '../OverlayTrigger'
import { useI18nContext } from '../_hooks/I18n'
import { addSubcomponents } from '../_utils/addSubcomponents'
import {
  StyledContainer,
  StyledTooltip,
  StyledTooltipButton,
} from './Tooltip.styles'
import type { TooltipContentProps, TooltipProps } from './Tooltip.types'

/** For the inline typography without all those button styles */
export const TooltipIconOnlyButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function TooltipDefaultTrigger({ type = 'button', ...props }, ref) {
  const I18n = useI18nContext()

  return (
    <StyledTooltipButton
      type={type}
      aria-label={I18n.t('core.form.field.tooltipHelp')}
      {...props}
      ref={ref}
    >
      <Help size="sm" />
    </StyledTooltipButton>
  )
})

const Content = React.forwardRef<HTMLSpanElement, TooltipContentProps>(
  function Content({ children, placement, ...props }, ref) {
    // For most consistent results: First render an empty aria-live container, then add content.
    // const [showContent, setShowContent] = React.useState(false)
    // React.useEffect(() => {
    //   const id = window.setTimeout(() => setShowContent(true), 10)
    //   return () => window.clearTimeout(id)
    // }, [])

    return (
      <StyledContainer aria-live="polite" ref={ref}>
        <StyledTooltip {...props}>
          {children}
          {/* {showContent ? children : null} */}
        </StyledTooltip>
      </StyledContainer>
    )
  }
)

const Tooltip_ = React.forwardRef<
  HTMLElement,
  OverlayTriggerProps & TooltipProps
>(function Tooltip(
  {
    children,
    initialIsVisible = false,
    overlay,
    placement = 'top',
    trigger = ['hover', 'focus'],
    ...props
  },
  ref
) {
  const overlayNode = React.isValidElement(overlay) ? (
    React.cloneElement(overlay as React.ReactElement, { placement })
  ) : (
    <Content>{overlay}</Content>
  )

  return (
    <OverlayTrigger
      {...{
        role: 'tooltip',
        passA11yPropsToOverlay: true,
        // TODO element this, it can cause failures for invalid use on some triggers.
        // OverlayTrigger should provide open state somehow, but that seems overkill for 80%? case of tooltip buttons
        // trackAriaExpanded: true,
        ...props,
        initialIsVisible,
        overlay: overlayNode,
        placement,
        ref,
        restoreFocusOnHide: false,
        trigger,
        shrinkOverlay: true,
        arrow: true,
      }}
    >
      {children}
    </OverlayTrigger>
  )
})

Tooltip_.displayName = 'Tooltip'

Content.displayName = 'Tooltip.Content'

/**

 We use tooltips to display additional descriptive information about an element
 or item to the user. For example, we could use them to explain a specific term
 or phrase, to denote the specific format required (e.g. mm/dd/yyy), or to
 clarify certain information (e.g. why a button is disabled).

 They can be displayed by hover or focus of an focusable element in the product or by
 hovering or focusing an icon button next to an element. Provide a focusable child.

 @since 10.19.0

 @see [Storybook](https://stories.core.procore.com/?path=/story/core-react_demos-tooltip--demo)

 @see [Design Guidelines](https://design.procore.com/tooltip)

 */
export const Tooltip = addSubcomponents(
  {
    Content,
  },
  Tooltip_
)
